import React, { useCallback } from 'react';
import { DraggableObject } from '../../../../../lib/canvas';
import {
	BaseEntityNames,
	BUILT_IN_BASE_ENTITY_IDS,
	CanvasEntityDisabledReason,
	ChangeSet,
	ChangeSetEntityChangeType,
	createStateFromTypeAndSubscribe,
	DefinitionEntityState,
	ExternalIntegrationNames,
	getFirstBaseEntityWithOwnBase,
	getIntegrationIdFromDefinitionEntity,
	LiteralValueState,
	PrimitiveTypes,
	ProjectState,
	StateMutationAction,
} from '@elyx-code/project-logic-tree';
import { EditorService } from '../../../../../services/editor';
import CanvasPopupBaseComponent from '../../../../../lib/popup/CanvasPopupBaseComponent';
import { useCanvasPopup } from '../../../../../services/editor/CanvasPopup';
import './restablish-connection-footer-action.css';
import ReconnectIcon from '../../../../../assets/sync.svg';
import { Logger } from '@elyx-code/common-ts-utils';
import { getCanvasEntityDOMId } from '../../../../../services/editor/ui';

export function getRestablishConnectionFooterActionDOMId(
	entity: DefinitionEntityState,
): string {
	return getCanvasEntityDOMId(entity) + '--restablish-connection-footer-action';
}

interface IRestablishConnectionFooterActionProps {
	draggableObject: DraggableObject;
	entity: DefinitionEntityState;
	project: EditorService;
	parentDisabledReasons: CanvasEntityDisabledReason[];
}

export default function RestablishConnectionFooterAction(
	props: IRestablishConnectionFooterActionProps
) {
	const id = getRestablishConnectionFooterActionDOMId(props.entity);

	const project = props.project;
	const entity = props.entity;

	const resolveTooltipHelperText = (): string => {
		return 'Click to establish a new connection to this external integration';
	};

	const disabled = !!props.parentDisabledReasons.length;

	const memoizedCanvasPopupBody = useCallback(
		() => (
			<CanvasPopupBaseComponent
				containerClasses={['tooltip', 'tooltip--large-font']}
				id={id + '--tooltip--popup--body'}
			>
				{disabled ? (
					<div style={{ textAlign: 'center' }}>
						<p style={{ margin: 0 }}>
							Re-establishing a connection is currently not possible
						</p>
					</div>
				) : (
					<div style={{ textAlign: 'center' }}>
						<p style={{ margin: 0 }}>{resolveTooltipHelperText()}</p>
					</div>
				)}
			</CanvasPopupBaseComponent>
		),
		[disabled]
	);

	const canvasPopupTooltip = useCanvasPopup(
		{
			editor: props.project,
			id: id + '--tooltip',
			anchorQuerySelector: '#' + id,
			arrowClasses: ['tooltip-arrow'],
			parentCanvasObject: props.draggableObject,
			placement: 'top',
			arrowContainerSelector: '#' + id + '--tooltip--popup--body',
		},
		memoizedCanvasPopupBody,
	).setupHover({
		closeDelay: 80,
		openDelay: 600,
	});

	const baseEntity = getFirstBaseEntityWithOwnBase(
		entity,
		BaseEntityNames.EXTERNAL_INTEGRATION_CONNECTION
	);

	const builtInConnectionPropertyId =
		BUILT_IN_BASE_ENTITY_IDS['built-in-base-entity'][
			baseEntity.id as 'BUILT_IN_EXTERNAL_INTEGRATION_CONNECTION_ENTITY'
		].properties.connectionId.id;

	const integrationName: ExternalIntegrationNames | null =
		getIntegrationIdFromDefinitionEntity(entity);

	if (!integrationName) {
		Logger.warn(
			`The integration name for the entity ${props.entity.id} could not be resolved.`
		);
		return null;
	}

	return (
		<button
			id={id}
			ref={canvasPopupTooltip.anchorRef}
			onClick={() => {
				if (disabled) {
					return;
				}

				project.integrationsService.connect(integrationName, {
					// Update the connection entity with the new connection data
					onSuccess: async (connection) => {
						Logger.log('Connection created successfully:', connection);

						const changeSet = project.logic.addChangeSet(
							new ChangeSet(
								project.logic,
								ProjectState.sessionAuthor,
								new Date().toISOString(),
								entity,
								false,
								StateMutationAction.RestablishExternalAccountConnection
							)
						);

						const connectionIdProperty = entity.properties.find(
							(property) =>
								property.implements.find(
									(impl) => impl.id === builtInConnectionPropertyId
								) ||
								// It could be that this is a specific connection entity, and therefore itself extends the base connection entity
								property.implements.find(
									(impl) => impl.extends?.id === builtInConnectionPropertyId
								)
						);

						let connectionIdLiteralValueInstance =
							connectionIdProperty?.getDefaultValue();

						if (!connectionIdLiteralValueInstance) {
							const newLiteralValueTransfer = LiteralValueState.new(changeSet);
							newLiteralValueTransfer.name = PrimitiveTypes.String;
							newLiteralValueTransfer.parent =
								connectionIdProperty.toReference();

							const newLiteralValueInstance = createStateFromTypeAndSubscribe(
								newLiteralValueTransfer,
								project.logic
							) as LiteralValueState;

							newLiteralValueInstance.hydrateAncestors();

							changeSet.add(
								newLiteralValueInstance,
								ChangeSetEntityChangeType.Added
							);

							newLiteralValueInstance.addSelfToProject(changeSet);
							newLiteralValueInstance.initChildren(changeSet);

							connectionIdLiteralValueInstance = newLiteralValueInstance;
						}

						connectionIdLiteralValueInstance.metaSync(
							{
								value: connection.connection_id,
							},
							changeSet
						);

						await project.renderAndCloseChangeSet(changeSet);
					},
				});
			}}
			className={['canvas-draggable-card__footer__item'].join(' ')}
		>
			<img
				src={ReconnectIcon}
				style={{
					width: 16,
					height: 16,
					opacity: disabled ? 0.2 : 0.8,
				}}
				alt='Reconnect Icon'
			/>
		</button>
	);
}
